import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../styles/components/PlaybackPanel.css';

interface Speaker {
  id: number;
  name: string;
}

interface Moment {
  id: string;
  name: string;
  time: number;
}

interface TimelineSegment {
  id: string;
  startTime: number;
  endTime: number;
  type: 'agent' | 'customer' | 'silence';
  speaker: Speaker;
}

interface PlaybackPanelProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  audioSrc: string;
  currentTranscriptId?: string;
  onTimeUpdate: (time: number) => void;
  onPlayingSegmentChange: (segmentId: string) => void;
  transcriptTimestamps: { id: string; startTime: number; endTime: number; speaker: Speaker }[];
  moments?: Moment[];
  totalDuration: number;
}

// Type declarations for global window property
declare global {
  interface Window {
    _globalAudioPlayer: any;
  }
}

// Type definitions for our audio player
interface AudioPlayerListener {
  id: number;
  type: string;
  callback: (data: any) => void;
}

interface AudioPlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  playbackRate: number;
}

interface AudioPlayer {
  loadSource: (src: string) => void;
  play: () => void;
  pause: () => void;
  seek: (timeInSeconds: number) => boolean;
  setVolume: (newVolume: number) => void;
  setPlaybackRate: (newRate: number) => void;
  getState: () => AudioPlayerState;
  addListener: (type: string, callback: (data: any) => void) => number;
  removeListener: (id: number) => void;
}

// Create global audio player
const createAudioPlayer = (): AudioPlayer => {
  // Only create once
  if (window._globalAudioPlayer) {
    return window._globalAudioPlayer;
  }
  
  console.log("Creating global audio player instance");
  
  // Create an actual DOM element and append to document
  const audioElement = document.createElement('audio');
  audioElement.id = 'global-audio-player';
  audioElement.style.display = 'none';
  document.body.appendChild(audioElement);
  
  // State variables
  let currentSource = '';
  let isPlaying = false;
  let currentTime = 0;
  let duration = 0;
  let volume = 0.8;
  let playbackRate = 1;
  let listeners: AudioPlayerListener[] = [];
  
  // Set initial properties
  audioElement.preload = 'auto';
  audioElement.volume = volume;
  audioElement.playbackRate = playbackRate;
  
  // Setup event listeners
  audioElement.addEventListener('loadedmetadata', () => {
    duration = audioElement.duration;
    console.log("Audio metadata loaded, duration:", duration);
    notifyListeners('durationchange', duration);
  });
  
  audioElement.addEventListener('timeupdate', () => {
    currentTime = audioElement.currentTime;
    notifyListeners('timeupdate', currentTime);
  });
  
  audioElement.addEventListener('play', () => {
    isPlaying = true;
    notifyListeners('statechange', { isPlaying });
  });
  
  audioElement.addEventListener('pause', () => {
    isPlaying = false;
    notifyListeners('statechange', { isPlaying });
  });
  
  audioElement.addEventListener('ended', () => {
    isPlaying = false;
    notifyListeners('statechange', { isPlaying });
  });
  
  // Utility to notify all listeners
  const notifyListeners = (type: string, data: any): void => {
    listeners.forEach(listener => {
      if (listener.type === type || listener.type === 'all') {
        listener.callback(data);
      }
    });
  };
  
  // Public API
  const player: AudioPlayer = {
    loadSource: (src: string): void => {
      if (src !== currentSource) {
        console.log("Loading new source:", src);
        currentSource = src;
        audioElement.src = src;
        audioElement.load();
      }
    },
    
    play: (): void => {
      console.log("Play command received");
      audioElement.play()
        .then(() => console.log("Playback started successfully"))
        .catch(err => console.error("Playback failed:", err));
    },
    
    pause: (): void => {
      console.log("Pause command received");
      audioElement.pause();
    },
    
    seek: (timeInSeconds: number): boolean => {
      console.log("Seek command received:", timeInSeconds);
      try {
        const wasPlaying = !audioElement.paused;
        
        // Always pause before seeking to avoid race conditions
        audioElement.pause();
        
        // Try to set time
        audioElement.currentTime = timeInSeconds;
        currentTime = timeInSeconds;
        
        // Resume if it was playing
        if (wasPlaying) {
          setTimeout(() => audioElement.play(), 50);
        }
        
        // Notify immediately for UI updates
        notifyListeners('timeupdate', timeInSeconds);
        
        return true;
      } catch (err) {
        console.error("Seek failed:", err);
        return false;
      }
    },
    
    setVolume: (newVolume: number): void => {
      volume = newVolume;
      audioElement.volume = volume;
    },
    
    setPlaybackRate: (newRate: number): void => {
      playbackRate = newRate;
      audioElement.playbackRate = playbackRate;
    },
    
    getState: (): AudioPlayerState => {
      return {
        isPlaying: !audioElement.paused,
        currentTime: audioElement.currentTime,
        duration: audioElement.duration || 0,
        volume,
        playbackRate
      };
    },
    
    addListener: (type: string, callback: (data: any) => void): number => {
      const id = Date.now() + Math.random();
      listeners.push({ id, type, callback });
      return id;
    },
    
    removeListener: (id: number): void => {
      listeners = listeners.filter(listener => listener.id !== id);
    }
  };
  
  // Store in window for persistence
  window._globalAudioPlayer = player;
  return player;
};

const PlaybackPanelGlobal: React.FC<PlaybackPanelProps> = ({
  audioRef,
  audioSrc,
  currentTranscriptId,
  onTimeUpdate,
  onPlayingSegmentChange,
  transcriptTimestamps,
  moments = [],
  totalDuration
}) => {
  // Get the global audio player
  const audioPlayerRef = useRef<AudioPlayer>(createAudioPlayer());
  
  // Local state for UI
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(totalDuration || 0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(0.8);
  const [hoverPosition, setHoverPosition] = useState<number | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Refs
  const timelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const seekCheckIntervalRef = useRef<number | null>(null);
  const wasPlayingRef = useRef(false);
  
  // Storage key for persisting position
  const STORAGE_KEY = `audio-playback-time-${currentTranscriptId || 'default'}`;
  
  // Initialize the audio player
  useEffect(() => {
    const player = audioPlayerRef.current;
    
    // Set up event listeners
    const timeUpdateId = player.addListener('timeupdate', (time: number) => {
      setCurrentTime(time);
    });
    
    const durationChangeId = player.addListener('durationchange', (newDuration: number) => {
      setDuration(newDuration);
    });
    
    const stateChangeId = player.addListener('statechange', (state: { isPlaying: boolean }) => {
      setIsPlaying(state.isPlaying);
    });
    
    // Load the source
    player.loadSource(audioSrc);
    
    // Restore previous time if available
    // const savedTime = sessionStorage.getItem(STORAGE_KEY);
    // if (savedTime) {
    //   const time = parseFloat(savedTime);
    //   if (!isNaN(time) && time > 0) {
    //     console.log("Restoring saved position:", time);
    //     player.seek(time);
    //   }
    // }
    
    // Set volume and playback rate
    player.setVolume(volume);
    player.setPlaybackRate(playbackRate);
    
    // Clean up
    return () => {
      player.removeListener(timeUpdateId);
      player.removeListener(durationChangeId);
      player.removeListener(stateChangeId);
    };
  }, [audioSrc, currentTranscriptId]);
  
  // Update volume and playback rate when changed
  useEffect(() => {
    audioPlayerRef.current.setVolume(volume);
  }, [volume]);
  
  useEffect(() => {
    audioPlayerRef.current.setPlaybackRate(playbackRate);
  }, [playbackRate]);
  
  // Handle playback toggle
  const handlePlayPause = () => {
    const player = audioPlayerRef.current;
    if (isPlaying) {
      player.pause();
    } else {
      player.play();
    }
  };
  
  // Progress click handler - with extensive logging and checks
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    
    console.log('Clicked progress bar at position:', percentage, 'Setting time to:', newTime);
    
    // Immediately update UI
    setCurrentTime(newTime);
    
    // Store the time in session storage
    sessionStorage.setItem(STORAGE_KEY, newTime.toString());
    
    // Remember if we were playing
    const player = audioPlayerRef.current;
    const playerState = player.getState();
    const wasPlaying = playerState.isPlaying;
    
    // IMPORTANT: First pause to avoid race conditions
    if (wasPlaying) {
      player.pause();
    }
    
    // IMPORTANT: Set local time in state BEFORE notifying parent
    try {
      console.log("About to perform seek to:", newTime);
      
      // IMPORTANT: Create a flag to check if we get interrupted
      let interrupted = false;
      
      // Use setTimeout to ensure the audio element operation happens BEFORE parent is notified
      setTimeout(() => {
        if (interrupted) {
          console.warn("Seek operation was interrupted!");
          return;
        }
        
        try {
          // Try to set the time directly
          const seekSuccess = player.seek(newTime);
          console.log("Seek result:", seekSuccess);
          
          // IMPORTANT: Only notify parent if seek was successful
          if (seekSuccess) {
            console.log("Now notifying parent of time update:", newTime * 1000);
            if (onTimeUpdate) {
              onTimeUpdate(newTime * 1000);
            }
            
            // Resume playback if it was playing
            if (wasPlaying) {
              setTimeout(() => {
                player.play();
              }, 50);
            }
          }
        } catch (err) {
          console.error("Error during seek:", err);
        }
      }, 5);
      
      // Set a flag if we're interrupted before completing
      return () => {
        interrupted = true;
      };
    } catch (err) {
      console.error("Unexpected error handling click:", err);
    }
  };
  
  // Simplified segment click using same techniques
  const handleSegmentClick = (segment: TimelineSegment) => {
    const timeInSeconds = segment.startTime / 1000;
    
    // Immediately update UI
    setCurrentTime(timeInSeconds);
    
    // Store in session storage
    sessionStorage.setItem(STORAGE_KEY, timeInSeconds.toString());
    
    // Remember if we were playing
    const player = audioPlayerRef.current;
    const playerState = player.getState();
    const wasPlaying = playerState.isPlaying;
    
    // IMPORTANT: First pause to avoid race conditions
    if (wasPlaying) {
      player.pause();
    }
    
    // Use setTimeout to ensure the audio element operation happens BEFORE parent is notified
    setTimeout(() => {
      try {
        // Try to set the time directly
        const seekSuccess = player.seek(timeInSeconds);
        
        // IMPORTANT: Only notify parent if seek was successful
        if (seekSuccess) {
          console.log("Now notifying parent of segment time update:", segment.startTime);
          
          // Wait a tiny bit before notifying
          setTimeout(() => {
            if (onTimeUpdate) {
              onTimeUpdate(segment.startTime);
            }
            
            if (!segment.id.startsWith('silence-')) {
              onPlayingSegmentChange(segment.id);
            }
            
            // Resume playback if it was playing
            if (wasPlaying) {
              player.play();
            }
          }, 50);
        }
      } catch (err) {
        console.error("Error during segment seek:", err);
      }
    }, 5);
  };

  // Generate timeline segments
  const timelineSegments: TimelineSegment[] = useMemo(() => {
    let segments = [...transcriptTimestamps].map(item => {
      // Determine segment type based on id and speaker
      let type: 'agent' | 'customer' | 'silence';
      
      if (item.id.startsWith('silence-') || item.id.includes('silence')) {
        type = 'silence';
      }
      else if (
        item.speaker.name.toLowerCase().includes('agent') ||
        item.speaker.id === 0
      ) {
        type = 'agent';
      }
      else {
        type = 'customer';
      }
      
      return {
        id: item.id,
        startTime: item.startTime,
        endTime: item.endTime,
        speaker: item.speaker,
        type
      };
    });
    
    // Add silence segments if none exist
    const silenceSegments = segments.filter(s => s.type === 'silence');
    if (silenceSegments.length === 0) {
      // Sort segments chronologically
      segments.sort((a, b) => a.startTime - b.startTime);
      
      // Find gaps between segments
      const silences: TimelineSegment[] = [];
      for (let i = 0; i < segments.length - 1; i++) {
        const gap = segments[i+1].startTime - segments[i].endTime;
        if (gap > 2000) { // 2 seconds or greater gap
          silences.push({
            id: `silence-auto-${i}`,
            startTime: segments[i].endTime,
            endTime: segments[i+1].startTime,
            type: 'silence',
            speaker: { id: 999, name: 'Silence' }
          });
        }
      }
      
      // Add silences and re-sort
      segments = [...segments, ...silences].sort((a, b) => a.startTime - b.startTime);
    }
    
    return segments;
  }, [transcriptTimestamps]);

  // Handle moment clicks - simplified
  const handleMomentClick = (time: number) => {
    if (!audioPlayerRef.current) return;
    
    const timeInSeconds = time / 1000;
    
    // Store in session storage for persistence
    sessionStorage.setItem(STORAGE_KEY, timeInSeconds.toString());
    
    // Nitin
    if (transcriptTimestamps && transcriptTimestamps.length > 0) {
      const currentSegment = transcriptTimestamps.find(
        segment => currentTime >= segment.startTime && currentTime <= segment.endTime
      );
      
      console.log("handleMomentClick: Current segment:", currentSegment);
      if (currentSegment) {
        onPlayingSegmentChange(currentSegment.id);
      }
    }

    // Set time directly
    if (audioPlayerRef.current.seek(timeInSeconds)) {
      // Notify parent
      onTimeUpdate(time);
    }
  };

  // Format time display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate percentage for positioning
  const getPositionPercentage = (timeMs: number) => {
    return (timeMs / (duration * 1000)) * 100;
  };
  
  // Handle tooltip on hover
  const handleProgressHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!showTooltip) setShowTooltip(true);
    
    const rect = e.currentTarget.getBoundingClientRect();
    const hoverX = e.clientX - rect.left;
    const percentage = hoverX / rect.width;
    setHoverPosition(percentage * duration);
  };
  
  const handleProgressLeave = () => {
    setShowTooltip(false);
    setHoverPosition(null);
  };

  useEffect(() => {
    // Add this section to update the current playing segment when time changes
    if (transcriptTimestamps && transcriptTimestamps.length > 0) {
      const currentSegment = transcriptTimestamps.find(
        segment => currentTime >= segment.startTime && currentTime <= segment.endTime
      );
      
      if (currentSegment) {
        onPlayingSegmentChange(currentSegment.id);
      }
    }
    
    // Call onTimeUpdate so parent components are notified
    console.log("useEffect: calling onTimeUpdate", currentTime*1000);
    onTimeUpdate(currentTime*1000);
    
  }, [currentTime, transcriptTimestamps, onPlayingSegmentChange, onTimeUpdate]);

  return (
    <div className="playback-panel">
      {/* No audio element - it's managed globally */}
      
      {/* Timeline container */}
      <div className="master-timeline-container" ref={containerRef}>
        {/* Timeline labels */}
        <div className="timeline-labels-container">
          <div className="timeline-label agent-label">Agent</div>
          <div className="timeline-label customer-label">Customer</div>
        </div>
        
        {/* Play button */}
        <div className="play-control">
          <button 
            className="play-button"
            onClick={handlePlayPause}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? "⏸" : "▶"}
          </button>
        </div>
        
        {/* Timeline */}
        <div className="timeline-wrapper">
          {/* Agent moments */}
          <div className="timeline-layer agent-moments-layer">
            {moments.map((moment, index) => (
              <div 
                key={`moment-${index}`}
                className="timeline-moment"
                style={{
                  left: `${getPositionPercentage(moment.time)}%`,
                  position: 'absolute',
                  maxWidth: '100px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                onClick={() => handleMomentClick(moment.time)}
                title={moment.name}
              >
                <div className="moment-marker" style={{ 
                  width: '10px', 
                  height: '10px', 
                  background: '#ff5722',
                  borderRadius: '50%',
                  margin: '0 auto'
                }}></div>
                <div className="moment-label" style={{
                  fontSize: '10px',
                  textAlign: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%'
                }}>{moment.name}</div>
              </div>
            ))}
          </div>
          
          {/* Agent timeline */}
          <div className="timeline-layer agent-layer">
            {timelineSegments
              .filter(segment => segment.type === 'agent')
              .map((segment, index) => (
                <div 
                  key={`agent-${index}`}
                  className="timeline-segment agent"
                  style={{
                    left: `${getPositionPercentage(segment.startTime)}%`,
                    width: `${getPositionPercentage(segment.endTime - segment.startTime)}%`
                  }}
                  onClick={() => handleSegmentClick(segment)}
                  title={`${segment.speaker.name}: ${formatTime(segment.startTime/1000)} - ${formatTime(segment.endTime/1000)}`}
                />
              ))}
          </div>
          
          {/* Silence timeline */}
          <div className="timeline-layer silence-layer">
            {timelineSegments
              .filter(segment => segment.type === 'silence')
              .map((segment, index) => (
                <div 
                  key={`silence-${index}`}
                  className="timeline-segment silence"
                  style={{
                    left: `${getPositionPercentage(segment.startTime)}%`,
                    width: `${getPositionPercentage(segment.endTime - segment.startTime)}%`
                  }}
                  onClick={() => handleSegmentClick(segment)}
                  title={`Silence: ${formatTime(segment.startTime/1000)} - ${formatTime(segment.endTime/1000)}`}
                />
              ))}
          </div>
          
          {/* Customer timeline */}
          <div className="timeline-layer customer-layer">
            {timelineSegments
              .filter(segment => segment.type === 'customer')
              .map((segment, index) => (
                <div 
                  key={`customer-${index}`}
                  className="timeline-segment customer"
                  style={{
                    left: `${getPositionPercentage(segment.startTime)}%`,
                    width: `${getPositionPercentage(segment.endTime - segment.startTime)}%`
                  }}
                  onClick={() => handleSegmentClick(segment)}
                  title={`${segment.speaker.name}: ${formatTime(segment.startTime/1000)} - ${formatTime(segment.endTime/1000)}`}
                />
              ))}
          </div>
          
          {/* Playhead */}
          <div 
            className="timeline-playhead"
            style={{left: `${(currentTime / duration) * 100}%`}}
          />
          
          {/* Progress layer */}
          <div 
            className="audio-progress-layer" 
            onClick={handleProgressClick}
            onMouseMove={handleProgressHover}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={handleProgressLeave}
          >
            <div 
              className="audio-progress-fill"
              style={{width: `${(currentTime / duration) * 100}%`}}
            />
            
            {/* Tooltip */}
            {showTooltip && hoverPosition !== null && (
              <div 
                className="time-tooltip"
                style={{left: `${(hoverPosition / duration) * 100}%`}}
              >
                {formatTime(hoverPosition)}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Controls */}
      <div className="bottom-controls">
        <div className="playback-rate">
          <button 
            className={`rate-button ${playbackRate === 0.5 ? 'active' : ''}`}
            onClick={() => setPlaybackRate(0.5)}
          >
            0.5x
          </button>
          <button 
            className={`rate-button ${playbackRate === 1 ? 'active' : ''}`}
            onClick={() => setPlaybackRate(1)}
          >
            1x
          </button>
          <button 
            className={`rate-button ${playbackRate === 1.5 ? 'active' : ''}`}
            onClick={() => setPlaybackRate(1.5)}
          >
            1.5x
          </button>
          <button 
            className={`rate-button ${playbackRate === 2 ? 'active' : ''}`}
            onClick={() => setPlaybackRate(2)}
          >
            2x
          </button>
        </div>
        
        <div className="time-display">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
        
        <div className="volume-control">
          <label className="volume-label">Volume: {Math.round(volume * 100)}%</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="volume-slider"
            aria-label="Volume control"
          />
        </div>
      </div>
    </div>
  );
};

export default PlaybackPanelGlobal;